/** @file Command input from CommandForm values: required fields always, optional ones once edited. */
import type { FieldSpec } from './schemaFields';

/** The input object, plus the paths of JSON fields whose text does not parse. */
export interface BuiltInput {
  input: Record<string, unknown>;
  badJson: string[];
}

/** True when the field or anything below it was edited. */
function touched(field: FieldSpec, edited: ReadonlySet<string>): boolean {
  const key = field.path.join('.');
  for (const path of edited) if (path === key || path.startsWith(`${key}.`)) return true;
  return false;
}

/** A leaf as the command takes it: '' is null when nullable, left out when optional; JSON text is parsed. */
function leafValue(field: FieldSpec, raw: unknown, badJson: string[]): unknown {
  if (raw === null || (raw === '' && field.nullable)) return null;
  if (raw === '' && (!field.required || field.kind === 'json')) return undefined;
  if (field.kind !== 'json') return raw;
  try {
    return JSON.parse(typeof raw === 'string' ? raw : '') as unknown;
  } catch {
    badJson.push(field.path.join('.'));
    return undefined;
  }
}

/** One object level; a present group recurses, an absent nullable group is null. */
function level(fields: readonly FieldSpec[], values: ReadonlyMap<string, unknown>, edited: ReadonlySet<string>, badJson: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if (!field.required && !touched(field, edited)) continue;
    const raw = values.get(field.path.join('.'));
    const value = field.kind === 'group' ? (raw === null ? null : level(field.fields, values, edited, badJson)) : leafValue(field, raw, badJson);
    if (value !== undefined) out[field.key] = value;
  }
  return out;
}

/** Input for `fields` from path-keyed `values`; `edited` holds the paths the user changed. */
export function buildInput(fields: readonly FieldSpec[], values: ReadonlyMap<string, unknown>, edited: ReadonlySet<string>): BuiltInput {
  const badJson: string[] = [];
  return { input: level(fields, values, edited, badJson), badJson };
}
